import { Workflow, Bot, Settings2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";

const ServicesSection = () => {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();

  const services = [
    {
      icon: Workflow,
      titleKey: "services.workflow.title",
      descKey: "services.workflow.desc",
      features: ["services.workflow.f1", "services.workflow.f2", "services.workflow.f3", "services.workflow.f4"],
    },
    {
      icon: Bot,
      titleKey: "services.ai.title",
      descKey: "services.ai.desc",
      features: ["services.ai.f1", "services.ai.f2", "services.ai.f3", "services.ai.f4"],
    },
    {
      icon: Settings2,
      titleKey: "services.optimization.title",
      descKey: "services.optimization.desc",
      features: ["services.optimization.f1", "services.optimization.f2", "services.optimization.f3", "services.optimization.f4"],
    },
  ];

  return (
    <section id="services" className="synra-section relative bg-card/50">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--primary)) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      <div className="synra-container relative z-10">
        {/* Section Header */}
        <div className="synra-section-header">
          <span className="synra-section-label">
            {t("services.label")}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {t("services.title")}{' '}
            <span className="synra-gradient-text">{t("services.titleHighlight")}</span>
          </h2>
          <p className="mx-auto max-w-2xl text-center text-muted-foreground text-lg">
            {t("services.subtitle")}
          </p>
        </div>

        {/* Services Grid */}
        <div className="synra-equal-grid md:grid-cols-3">
          {services.map((service) => (
            <div
              key={service.titleKey}
              className="synra-card group relative overflow-hidden text-start"
            >
              {/* Hover Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative z-10 flex h-full flex-col">
                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors duration-300">
                  <service.icon className="w-7 h-7 text-primary" />
                </div>

                {/* Title */}
                <h3 className="font-heading font-semibold text-xl text-foreground mb-3">
                  {t(service.titleKey)}
                </h3>

                {/* Description */}
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {t(service.descKey)}
                </p>

                {/* Features */}
                <ul className="mb-6 flex-1 space-y-2">
                  {service.features.map((featureKey) => (
                    <li key={featureKey} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      <span>{t(featureKey)}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Button variant="synra-ghost" className="group/btn mt-auto h-auto justify-start p-0" onClick={() => navigate("/services")}>
                  <span>{t("common.learnMore")}</span>
                  <ArrowRight className={`w-4 h-4 transition-transform duration-200 group-hover/btn:translate-x-1 ${isRTL ? 'rtl-flip' : ''}`} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;